import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import swal from "sweetalert";
import {
  FaArrowLeft,
  FaBoxOpen,
  FaSave,
  FaImage,
  FaCloudUploadAlt,
  FaTrashAlt,
  FaExclamationTriangle,
  FaStar,
  FaFire,
  FaEye,
  FaShoppingCart,
  FaPlus
} from "react-icons/fa";
import ShopifySEOEditor from "../common/ShopifySEOEditor";
import ProductVariantsEditor, { catalogFromDraft } from "./ProductVariantsEditor";
import { checkImageFile, focusFirstError, hasApiErrors, readApiErrors } from "../../../util/formErrors";

export default function AddProduct() {
  const history = useHistory();
  const user = sessionStorage.getItem("id") || "1";

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState({});
  const [categoryList, setCategoryList] = useState([]);
  const [pictures, setPictures] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  const [allCheckBox, setCheckBoxes] = useState({
    featured: false,
    popular: false,
    on_store: true,
  });

  const [productInput, setProductInput] = useState({
    category_id: "",
    slug: "",
    name: "",
    discription: "",
    meta_title: "",
    meta_keyword: "",
    meta_descrip: "",
    selling_price: "",
    cost_price: "",
    qty: "",
    brand: "",
  });
  const [variants, setVariants] = useState({ options: [], items: [], error: "" });

  const [isCustomSlug, setIsCustomSlug] = useState(false);

  // Financial calculations
  const sellingPriceNum = Number(productInput.selling_price || 0);
  const unitCostNum = Number(productInput.cost_price || 0);
  const qtyNum = Number(productInput.qty || 0);
  const grossProfitPerUnit = sellingPriceNum - unitCostNum;
  const marginPercent =
    sellingPriceNum > 0 ? ((grossProfitPerUnit / sellingPriceNum) * 100).toFixed(1) : 0;
  const totalStockCost = qtyNum * unitCostNum;
  const totalValuation = qtyNum * sellingPriceNum;

  useEffect(() => {
    document.title = "Add Product — Admin Catalog";

    axios.get(`/api/all-category`).then((res) => {
      if (res.data.status === 200) {
        setCategoryList(res.data.category || []);
        if (res.data.category && res.data.category.length > 0) {
          setProductInput((prev) => ({
            ...prev,
            category_id: prev.category_id || res.data.category[0].id,
          }));
        }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const slugify = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[\s\W-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleInput = (e) => {
    e.persist();
    const { name, value } = e.target;

    setProductInput((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "name" && !isCustomSlug) {
        updated.slug = slugify(value);
      }
      return updated;
    });

    if (error[name]) {
      setError((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSlugChange = (e) => {
    setIsCustomSlug(true);
    setProductInput((prev) => ({
      ...prev,
      slug: slugify(e.target.value),
    }));
  };

  const handleCheckBox = (e) => {
    const { name, checked } = e.target;
    setCheckBoxes((prev) => ({ ...prev, [name]: checked }));
  };

  // Each photo is checked when chosen (same rules as the server); good ones are kept.
  const handleImageChange = (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    if (!chosen.length) return;
    const problems = [];
    const newFiles = chosen.filter((file) => {
      const problem = checkImageFile(file);
      if (problem) problems.push(problem);
      return !problem;
    });
    setPictures((prev) => [...prev, ...newFiles]);
    setImagePreviews((prev) => [...prev, ...newFiles.map((f) => URL.createObjectURL(f))]);
    setError((prev) => ({ ...prev, image: problems.join(" ") || null }));
  };

  const removeImageAt = (index) => {
    setPictures((prev) => prev.filter((_, idx) => idx !== index));
    setImagePreviews((prev) => prev.filter((_, idx) => idx !== index));
  };

  const submitProduct = (e) => {
    e.preventDefault();
    const sellingPrice = Number(productInput.selling_price || 0);
    const unitCost = Number(productInput.cost_price || 0);
    const built = catalogFromDraft(variants);
    const usingVariants = !built.error && built.options.length > 0;
    const effectivePrice = usingVariants ? built.selling : sellingPrice;
    const effectiveQty = usingVariants ? built.qty : Number(productInput.qty || 0);

    // Every problem at once, each under its own field.
    const found = {};
    if (!productInput.name.trim()) found.name = "Enter a product name.";
    if (!productInput.category_id) found.category_id = "Choose a category.";
    if (!productInput.brand.trim()) found.brand = "Enter a brand name.";
    if (built.error && (variants.options || []).some((option) => option.name || option.valuesText)) found.variants = built.error;
    if (!usingVariants && (!productInput.selling_price || sellingPrice <= 0)) {
      found.selling_price = "Enter a selling price greater than 0.";
    } else if (!usingVariants && unitCost > 0 && sellingPrice <= unitCost) {
      found.selling_price = `Selling Price (Rs. ${sellingPrice}) must be greater than Unit Cost (Rs. ${unitCost}).`;
      found.cost_price = `Unit Cost / Buying Price must be less than Selling Price (Rs. ${sellingPrice}).`;
    }
    if (pictures.length === 0) found.image = "Add at least one product photo.";
    if (Object.keys(found).length) {
      setError(found);
      focusFirstError();
      return;
    }

    setSubmitting(true);
    const finalSlug = productInput.slug || slugify(productInput.name);
    const finalMetaTitle = productInput.meta_title || productInput.name;

    const formData = new FormData();
    for (let i = 0; i < pictures.length; i++) {
      formData.append("image[]", pictures[i]);
    }
    formData.append("category_id", productInput.category_id);
    formData.append("slug", finalSlug);
    formData.append("name", productInput.name);
    formData.append("discription", productInput.discription || "");

    formData.append("meta_title", finalMetaTitle);
    formData.append("meta_keyword", productInput.meta_keyword || "");
    formData.append("meta_descrip", productInput.meta_descrip || "");

    const choiceCosts = (built.items || []).map((item) => Number(item.cost_price || 0)).filter((cost) => cost > 0);
    const rolledCost = choiceCosts.length ? Math.min(...choiceCosts) : 0;
    formData.append("selling_price", effectivePrice);
    formData.append("cost_price", usingVariants ? rolledCost : (productInput.cost_price || "0"));
    formData.append("qty", effectiveQty);
    formData.append("variants", JSON.stringify({
      options: usingVariants ? built.options : [],
      items: usingVariants ? built.items : [],
    }));
    formData.append("brand", productInput.brand);
    formData.append("featured", allCheckBox.featured ? "1" : "0");
    formData.append("popular", allCheckBox.popular ? "1" : "0");
    formData.append("status", allCheckBox.on_store ? "1" : "0");
    formData.append("store_visibility", allCheckBox.on_store ? "1" : "0");

    formData.append("created_by", user);

    // Errors arrive as a 200 reply with status 4xx, or as an HTTP error: show both under the fields.
    const showErrors = (source) => {
      const { message, fields } = readApiErrors(source);
      if (fields.original_price && !fields.cost_price) fields.cost_price = fields.original_price;
      setError(fields);
      focusFirstError();
      swal("Product not saved", message || "Please check the highlighted fields.", "error");
    };
    axios.post("/api/store-product", formData).then((res) => {
      setSubmitting(false);
      if (res.data.status === 200) {
        swal("Success", res.data.message || "Product added successfully", "success");
        history.push("/admin/view-product");
      } else if (hasApiErrors(res)) {
        showErrors(res);
      }
    }).catch((err) => {
      setSubmitting(false);
      showErrors(err);
    });
  };

  const hasChoices = (variants.options || []).length > 0;
  const variantPreview = catalogFromDraft(variants);
  const choicesReady = hasChoices && !variantPreview.error && (variantPreview.items || []).length > 0;
  const activePrice = hasChoices ? Number(variantPreview.selling || 0) : sellingPriceNum;
  const activeQty = hasChoices ? Number(variantPreview.qty || 0) : qtyNum;

  const productSteps = [
    {
      id: "product-step-name",
      label: "Write the product name",
      hint: "Customers see this title on the store.",
      done: productInput.name.trim() !== "",
    },
    {
      id: "product-step-photo",
      label: "Add a photo",
      hint: "The first photo is the one on the product card.",
      done: pictures.length > 0,
    },
    {
      id: "product-step-price",
      label: hasChoices ? "Set a price on each choice" : "Set the selling price",
      hint: hasChoices ? "Type the selling price, buying price, and stock on every row." : "This is the price customers pay.",
      done: hasChoices ? choicesReady : sellingPriceNum > 0,
    },
    {
      id: "product-step-stock",
      label: hasChoices ? "Enter the brand" : "Enter stock and brand",
      hint: hasChoices ? "Stock is already added up from each choice." : "Stock is how many you can sell. Brand is required.",
      done: hasChoices ? productInput.brand.trim() !== "" : String(productInput.qty) !== "" && productInput.brand.trim() !== "",
    },
    {
      id: "product-step-category",
      label: "Choose a category",
      hint: "This is where shoppers find the product.",
      done: String(productInput.category_id) !== "",
    },
    {
      id: "product-step-save",
      label: "Save the product",
      hint: allCheckBox.on_store
        ? "It will appear on the store after you save."
        : "It stays in your catalog until you turn on Show on the store.",
      done: false,
    },
  ];
  const nextStepIndex = productSteps.findIndex((step) => !step.done);

  const goToProductStep = (id) => {
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const selectedCategoryObj = categoryList.find(
    (c) => String(c.id) === String(productInput.category_id)
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-muted mt-2 small">Loading product catalog settings...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid px-0">
      {/* Top Header & Breadcrumbs */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-1 small">
              <li className="breadcrumb-item">
                <Link to="/admin/dashboard" className="text-decoration-none text-muted">
                  Dashboard
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/admin/view-product" className="text-decoration-none text-muted">
                  Products
                </Link>
              </li>
              <li className="breadcrumb-item active text-dark fw-semibold" aria-current="page">
                Add Product
              </li>
            </ol>
          </nav>
          <div className="d-flex align-items-center gap-2">
            <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.5px" }}>
              Add New Product
            </h3>
            <span className="admin-badge admin-badge-info">
              <FaBoxOpen size={11} className="me-1" /> Inventory Catalog
            </span>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Link
            to="/admin/view-product"
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-3"
          >
            <FaArrowLeft size={13} /> Discard
          </Link>
          <button
            type="button"
            onClick={submitProduct}
            disabled={submitting}
            className="btn btn-primary d-inline-flex align-items-center gap-2 fw-semibold px-4 py-2 shadow-sm rounded-3"
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FaSave size={14} /> Save Product
              </>
            )}
          </button>
        </div>
      </div>

      <form encType="multipart/form-data" onSubmit={submitProduct}>
        <div className="row g-4">
          {/* Main Column (8 cols): Title, Media, Pricing, Inventory, SEO */}
          <div className="col-12 col-lg-8">
            {/* 1. Title, Slug & Description */}
            <div className="admin-card mb-4 shadow-sm border-0" id="product-step-name">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                <span className="badge bg-primary rounded-circle" style={{ width: "1.6rem", height: "1.6rem", lineHeight: "1.15rem" }}>1</span>
                <h5 className="admin-card-title mb-0">Product name</h5>
              </div>
              <div className="admin-card-body p-4">
                <div className="mb-3">
                  <label className="form-label fw-semibold text-dark">
                    Product Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="e.g. Vintage Leather Crossbody Bag, Noise-Cancelling Headphones"
                    className={`form-control form-control-lg ${error.name ? "is-invalid" : ""}`}
                    onChange={handleInput}
                    value={productInput.name}
                    required
                  />
                  {error.name && <div className="invalid-feedback">{error.name}</div>}
                </div>

                {/* Slug handle */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="form-label fw-semibold text-dark mb-0 small">
                      URL Slug Handle
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsCustomSlug(!isCustomSlug)}
                      className="btn btn-link btn-sm text-decoration-none p-0"
                      style={{ fontSize: "12px" }}
                    >
                      {isCustomSlug ? "Auto-generate" : "Edit URL"}
                    </button>
                  </div>
                  <div className="input-group">
                    <span className="input-group-text bg-light text-muted small font-monospace">
                      /product/
                    </span>
                    <input
                      type="text"
                      name="slug"
                      disabled={!isCustomSlug}
                      placeholder="vintage-leather-crossbody-bag"
                      className={`form-control font-monospace ${error.slug ? "is-invalid" : ""}`}
                      onChange={handleSlugChange}
                      value={productInput.slug || slugify(productInput.name)}
                    />
                  </div>
                  {error.slug && <small className="text-danger d-block mt-1">{error.slug}</small>}
                </div>

                {/* Description */}
                <div className="mb-0">
                  <label className="form-label fw-semibold text-dark">Description</label>
                  <textarea
                    name="discription"
                    rows="5"
                    placeholder="Describe key features, materials, sizing, and benefits..."
                    className="form-control"
                    onChange={handleInput}
                    value={productInput.discription}
                  />
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <small className="text-muted" style={{ fontSize: "11.5px" }}>
                      Clear descriptions build customer trust and improve conversion rates.
                    </small>
                    <small className="text-muted" style={{ fontSize: "11.5px" }}>
                      {productInput.discription.length} characters
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Media & Product Images Gallery */}
            <div className="admin-card mb-4 shadow-sm border-0" id="product-step-photo">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary rounded-circle" style={{ width: "1.6rem", height: "1.6rem", lineHeight: "1.15rem" }}>2</span>
                  <h5 className="admin-card-title mb-0">Photo</h5>
                </div>
                <span className="badge bg-light text-muted border">
                  {pictures.length} {pictures.length === 1 ? "Image" : "Images"} Selected
                </span>
              </div>
              <div className="admin-card-body p-4">
                {/* Drag and Drop Upload Zone */}
                <label
                  htmlFor="product-image-upload"
                  className="d-flex flex-column align-items-center justify-content-center p-4 rounded-3 border-2 border-dashed bg-light text-center cursor-pointer w-100 mb-3"
                  style={{ borderStyle: "dashed", borderColor: "#cbd5e1", cursor: "pointer", transition: "all 0.2s" }}
                >
                  <FaCloudUploadAlt size={44} className="text-primary mb-2 opacity-75" />
                  <span className="fw-bold text-dark fs-6 mb-1">Click to browse or drop images here</span>
                  <span className="text-muted small mb-2">JPG, PNG, WebP or JPEG (You can select multiple images)</span>
                  <span className="btn btn-sm btn-outline-primary px-3 rounded-pill">
                    <FaPlus size={11} className="me-1" /> Add Images
                  </span>
                  <input
                    id="product-image-upload"
                    type="file"
                    name="image"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="d-none"
                    onChange={handleImageChange}
                  />
                </label>

                {error.image && <div className="field-error alert alert-danger py-2 px-3 mb-3 small" role="alert">{error.image}</div>}

                {/* Selected Images Grid */}
                {imagePreviews.length > 0 && (
                  <div>
                    <span className="small fw-semibold text-muted d-block mb-2">
                      Selected Images ({imagePreviews.length}):
                    </span>
                    <div className="row g-3">
                      {imagePreviews.map((previewUrl, idx) => (
                        <div key={idx} className="col-6 col-sm-4 col-md-3">
                          <div className="position-relative rounded-3 overflow-hidden border bg-white shadow-sm h-100">
                            {idx === 0 && (
                              <span
                                className="position-absolute top-0 start-0 m-1 badge bg-primary small fw-semibold"
                                style={{ fontSize: "9.5px", zIndex: 2 }}
                              >
                                ★ Cover Image
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => removeImageAt(idx)}
                              className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 p-1 rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: "22px", height: "22px", zIndex: 2 }}
                              title="Remove image"
                            >
                              <FaTrashAlt size={10} />
                            </button>
                            <div
                              className="d-flex align-items-center justify-content-center p-1"
                              style={{ height: "130px", background: "#f8fafc" }}
                            >
                              <img
                                src={previewUrl}
                                alt={`Product thumbnail ${idx + 1}`}
                                style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                              />
                            </div>
                            <div className="p-1.5 text-center border-top bg-white">
                              <small className="text-muted text-truncate d-block" style={{ fontSize: "11px" }}>
                                {pictures[idx]?.name || `Image #${idx + 1}`}
                              </small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="admin-card mb-4 shadow-sm border-0">
              <div className="admin-card-header bg-white py-3 border-bottom">
                <h5 className="admin-card-title mb-0">Sizes, colors, and other choices</h5>
              </div>
              <div className="admin-card-body p-4">
                {error.variants && <div className="field-error alert alert-danger py-2 px-3 mb-2 small" role="alert">{error.variants}</div>}
                <ProductVariantsEditor value={variants} onChange={setVariants} />
              </div>
            </div>

            {/* 3. Pricing & Financial Margin Intelligence */}
            <div className="admin-card mb-4 shadow-sm border-0" id="product-step-price">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary rounded-circle" style={{ width: "1.6rem", height: "1.6rem", lineHeight: "1.15rem" }}>3</span>
                  <h5 className="admin-card-title mb-0">Price</h5>
                </div>
                <span className="badge bg-success bg-opacity-10 text-success fw-semibold">
                  Profit Engine
                </span>
              </div>
              <div className="admin-card-body p-4">
                {hasChoices ? (
                  <div className="alert alert-light border mb-3">
                    <strong>Selling price, buying price, and stock are on each choice above.</strong>
                    <span className="d-block mt-1">
                      {choicesReady
                        ? `Customers see prices from Rs. ${activePrice.toLocaleString()}. Total stock is ${activeQty}.`
                        : "Fill in the selling price, buying price, and stock on every row."}
                    </span>
                  </div>
                ) : (
                <div className="row g-3 mb-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold text-dark">
                      Selling Price (PKR) <span className="text-danger">*</span>
                    </label>
                    <div className="input-group input-group-lg">
                      <span className="input-group-text bg-light text-muted fw-semibold">Rs.</span>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        name="selling_price"
                        className={`form-control font-monospace ${error.selling_price ? "is-invalid" : ""}`}
                        placeholder="e.g. 2500"
                        onChange={handleInput}
                        value={productInput.selling_price}
                        required
                      />
                    </div>
                    {error.selling_price && (
                      <small className="text-danger d-block mt-1">{error.selling_price}</small>
                    )}
                  </div>
                </div>
                )}

                {!hasChoices && (
                <div className="row g-3 mb-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold text-dark">
                      Unit Cost / Buying Price (PKR)
                    </label>
                    <div className="input-group input-group-lg">
                      <span className="input-group-text bg-light text-muted fw-semibold">Rs.</span>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        name="cost_price"
                        className={`form-control font-monospace ${error.cost_price ? "is-invalid" : ""}`}
                        placeholder="e.g. 1500"
                        onChange={handleInput}
                        value={productInput.cost_price}
                      />
                    </div>
                    <small className="text-muted d-block mt-1" style={{ fontSize: "11.5px" }}>
                      Unit acquisition/buying cost (PKR). Used for margin & profit calculation.
                    </small>
                    {error.cost_price && (
                      <small className="text-danger d-block mt-1">{error.cost_price}</small>
                    )}
                  </div>
                </div>
                )}

                {/* Price warning if selling price is less than or equal to unit cost */}
                {!hasChoices && unitCostNum > 0 && sellingPriceNum > 0 && sellingPriceNum <= unitCostNum && (
                  <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3 small">
                    <FaExclamationTriangle size={16} className="flex-shrink-0" />
                    <span>
                      <strong>Negative or Zero Margin Alert:</strong> Selling Price (Rs. {sellingPriceNum.toLocaleString()}) must be higher than Unit Cost (Rs. {unitCostNum.toLocaleString()}) to ensure profitability.
                    </span>
                  </div>
                )}

                {!hasChoices && (
                <div className="p-3 bg-light rounded-3 border">
                  <div className="row g-3 text-center text-md-start align-items-center">
                    <div className="col-6 col-md-3 border-end">
                      <span className="text-muted small d-block" style={{ fontSize: "11.5px" }}>
                        Profit Per Unit
                      </span>
                      <span
                        className={`fw-bold font-monospace fs-6 ${
                          grossProfitPerUnit >= 0 ? "text-success" : "text-danger"
                        }`}
                      >
                        Rs. {grossProfitPerUnit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>

                    <div className="col-6 col-md-3 border-end">
                      <span className="text-muted small d-block" style={{ fontSize: "11.5px" }}>
                        Gross Margin
                      </span>
                      <span
                        className={`badge rounded-pill ${
                          Number(marginPercent) >= 30
                            ? "bg-success"
                            : Number(marginPercent) > 0
                            ? "bg-primary"
                            : "bg-danger"
                        }`}
                      >
                        {marginPercent}% Margin
                      </span>
                    </div>

                    <div className="col-6 col-md-3 border-end">
                      <span className="text-muted small d-block" style={{ fontSize: "11.5px" }}>
                        Total Retail Valuation
                      </span>
                      <span className="fw-bold text-dark font-monospace">
                        Rs. {totalValuation.toLocaleString()}
                      </span>
                    </div>

                    <div className="col-6 col-md-3">
                      <span className="text-muted small d-block" style={{ fontSize: "11.5px" }}>
                        Total Stock Cost (COGS)
                      </span>
                      <span className="fw-bold text-secondary font-monospace">
                        Rs. {totalStockCost.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* 4. Inventory & Stock Management */}
            <div className="admin-card mb-4 shadow-sm border-0" id="product-step-stock">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                <span className="badge bg-primary rounded-circle" style={{ width: "1.6rem", height: "1.6rem", lineHeight: "1.15rem" }}>4</span>
                <h5 className="admin-card-title mb-0">{hasChoices ? "Brand" : "Stock and brand"}</h5>
              </div>
              <div className="admin-card-body p-4">
                <div className="row g-3">
                  {!hasChoices && (
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold text-dark">
                      Initial Stock Quantity <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      name="qty"
                      placeholder="e.g. 50"
                      className={`form-control form-control-lg ${error.qty ? "is-invalid" : ""}`}
                      onChange={handleInput}
                      value={productInput.qty}
                      required
                    />
                    {error.qty && <small className="text-danger d-block mt-1">{error.qty}</small>}
                    <small className="text-muted d-block mt-1" style={{ fontSize: "11.5px" }}>
                      How many you can sell when every customer buys the same product.
                    </small>
                  </div>
                  )}

                  <div className={hasChoices ? "col-12" : "col-12 col-md-6"}>
                    <label className="form-label fw-semibold text-dark">Brand / Manufacturer <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      name="brand"
                      placeholder="e.g. Zara, Nike"
                      className={`form-control form-control-lg ${error.brand ? "is-invalid" : ""}`}
                      onChange={handleInput}
                      value={productInput.brand}
                      required
                    />
                    {error.brand && <small className="text-danger d-block mt-1">{error.brand}</small>}
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Shopify-Style Search Engine Optimization (SEO) */}
            <ShopifySEOEditor
              itemName={productInput.name}
              itemBrand={productInput.brand}
              itemCategoryName={categoryList.find((c) => String(c.id) === String(productInput.category_id))?.name}
              itemDescription={productInput.discription}
              itemSlug={productInput.slug}
              metaTitle={productInput.meta_title}
              metaDescription={productInput.meta_descrip}
              metaKeywords={productInput.meta_keyword}
              type="product"
              onChange={(updated) => {
                setProductInput((prev) => ({
                  ...prev,
                  ...updated,
                }));
              }}
            />
          </div>

          <div className="col-12 col-lg-4">
            <div className="admin-card mb-4 shadow-sm border-0">
              <div className="admin-card-header bg-white py-3 border-bottom">
                <h5 className="admin-card-title mb-0">Follow these steps</h5>
              </div>
              <div className="admin-card-body p-3">
                <ol className="list-unstyled mb-0 d-flex flex-column gap-2">
                  {productSteps.map((step, index) => {
                    const current = index === nextStepIndex;
                    return (
                      <li key={step.id}>
                        <button
                          type="button"
                          onClick={() => goToProductStep(step.id)}
                          className="btn btn-light border w-100 text-start d-flex align-items-start gap-2 p-2"
                        >
                          <span
                            className={`badge rounded-circle flex-shrink-0 ${step.done ? "bg-success" : current ? "bg-primary" : "bg-secondary"}`}
                            style={{ width: "1.5rem", height: "1.5rem", lineHeight: "1.15rem" }}
                          >
                            {step.done ? "✓" : index + 1}
                          </span>
                          <span>
                            <span className="d-block fw-semibold text-dark" style={{ fontSize: "13px" }}>{step.label}</span>
                            {current && (
                              <span className="d-block text-muted" style={{ fontSize: "12px" }}>{step.hint}</span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </div>

            <div className="admin-card mb-4 shadow-sm border-0" id="product-step-category">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary rounded-circle" style={{ width: "1.6rem", height: "1.6rem", lineHeight: "1.15rem" }}>5</span>
                  <h5 className="admin-card-title mb-0">Category</h5>
                </div>
                <Link
                  to="/admin/add-category"
                  className="btn btn-link btn-sm text-decoration-none p-0 fw-semibold"
                  style={{ fontSize: "12px" }}
                >
                  + New Category
                </Link>
              </div>
              <div className="admin-card-body p-4">
                <label className="form-label fw-semibold text-dark">
                  Category <span className="text-danger">*</span>
                </label>
                <select
                  name="category_id"
                  className={`form-select ${error.category_id ? "is-invalid" : ""}`}
                  onChange={handleInput}
                  value={productInput.category_id}
                  required
                >
                  <option value="">Select a Category</option>
                  {categoryList.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {error.category_id && (
                  <small className="text-danger d-block mt-1">{error.category_id}</small>
                )}
                <small className="text-muted d-block mt-1" style={{ fontSize: "11.5px" }}>
                  Shoppers find this product under this category.
                </small>
              </div>
            </div>

            <div className="admin-card mb-4 shadow-sm border-0">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                <FaEye className="text-primary" />
                <h5 className="admin-card-title mb-0">After you save</h5>
              </div>
              <div className="admin-card-body p-4">
                <div className="form-check form-switch mb-3 d-flex align-items-center justify-content-between ps-0">
                  <div>
                    <label
                      htmlFor="product-status-switch"
                      className="form-check-label fw-semibold text-dark d-block mb-0 cursor-pointer"
                    >
                      Show on the store
                    </label>
                    <small className="text-muted d-block" style={{ fontSize: "12px" }}>
                      {allCheckBox.on_store ? "Customers can find and buy this product." : "Saved in your catalog. Customers will not see it."}
                    </small>
                  </div>
                  <input
                    id="product-status-switch"
                    type="checkbox"
                    name="on_store"
                    className="form-check-input ms-2"
                    style={{ width: "2.8em", height: "1.4em", cursor: "pointer" }}
                    checked={allCheckBox.on_store}
                    onChange={handleCheckBox}
                  />
                </div>

                <hr className="my-3" />

                {/* Featured Product toggle */}
                <div className="form-check form-switch mb-3 d-flex align-items-center justify-content-between ps-0">
                  <div>
                    <label
                      htmlFor="product-featured-switch"
                      className="form-check-label fw-semibold text-dark d-block mb-0 cursor-pointer d-flex align-items-center gap-1.5"
                    >
                      <FaStar className="text-warning" size={13} /> Featured Product
                    </label>
                    <small className="text-muted d-block" style={{ fontSize: "12px" }}>
                      Highlight on store home page
                    </small>
                  </div>
                  <input
                    id="product-featured-switch"
                    type="checkbox"
                    name="featured"
                    className="form-check-input ms-2"
                    style={{ width: "2.8em", height: "1.4em", cursor: "pointer" }}
                    checked={allCheckBox.featured}
                    onChange={handleCheckBox}
                  />
                </div>

                {/* Popular / Trending toggle */}
                <div className="form-check form-switch mb-0 d-flex align-items-center justify-content-between ps-0">
                  <div>
                    <label
                      htmlFor="product-popular-switch"
                      className="form-check-label fw-semibold text-dark d-block mb-0 cursor-pointer d-flex align-items-center gap-1.5"
                    >
                      <FaFire className="text-danger" size={13} /> Popular / Trending
                    </label>
                    <small className="text-muted d-block" style={{ fontSize: "12px" }}>
                      Add "Best Seller" badge in catalog
                    </small>
                  </div>
                  <input
                    id="product-popular-switch"
                    type="checkbox"
                    name="popular"
                    className="form-check-input ms-2"
                    style={{ width: "2.8em", height: "1.4em", cursor: "pointer" }}
                    checked={allCheckBox.popular}
                    onChange={handleCheckBox}
                  />
                </div>
              </div>
            </div>

            {/* Live Storefront Card Preview */}
            <div className="admin-card mb-4 shadow-sm border-0">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <FaEye className="text-info" />
                  <h5 className="admin-card-title mb-0">Storefront Live Preview</h5>
                </div>
                <span className="badge bg-light text-muted border">Customer View</span>
              </div>
              <div className="admin-card-body p-3 bg-light bg-opacity-50">
                <div
                  className="card border rounded-3 overflow-hidden shadow-sm mx-auto bg-white"
                  style={{ maxWidth: "280px" }}
                >
                  {/* Thumbnail Image Container */}
                  <div
                    className="position-relative d-flex align-items-center justify-content-center bg-light"
                    style={{ height: "180px", overflow: "hidden" }}
                  >
                    {imagePreviews.length > 0 ? (
                      <img
                        src={imagePreviews[0]}
                        alt="Product preview"
                        style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <div className="text-center text-muted">
                        <FaImage size={36} className="opacity-40 mb-1" />
                        <span className="d-block small" style={{ fontSize: "11px" }}>No image uploaded</span>
                      </div>
                    )}

                    {/* Floating Badges */}
                    <div className="position-absolute top-0 start-0 m-2 d-flex flex-column gap-1">
                      {allCheckBox.featured && (
                        <span className="badge bg-warning text-dark shadow-sm" style={{ fontSize: "9px" }}>
                          ★ Featured
                        </span>
                      )}
                      {allCheckBox.popular && (
                        <span className="badge bg-danger text-white shadow-sm" style={{ fontSize: "9px" }}>
                          🔥 Best Seller
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Body Content */}
                  <div className="p-3">
                    <span
                      className="text-muted text-uppercase fw-bold d-block mb-1"
                      style={{ fontSize: "10px", letterSpacing: "0.5px" }}
                    >
                      {productInput.brand || "Brand"} • {selectedCategoryObj?.name || "Category"}
                    </span>
                    <h6
                      className="fw-bold text-dark text-truncate mb-2"
                      style={{ fontSize: "14px" }}
                      title={productInput.name}
                    >
                      {productInput.name || "Untitled Product"}
                    </h6>

                    <div className="d-flex align-items-baseline gap-2 mb-2">
                      <span className="fs-6 fw-bold text-primary font-monospace">
                        Rs. {activePrice > 0 ? activePrice.toLocaleString() : "0.00"}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                      <span
                        className={`badge ${
                          activeQty > 5 ? "bg-success bg-opacity-10 text-success" : activeQty > 0 ? "bg-warning bg-opacity-10 text-warning" : "bg-danger bg-opacity-10 text-danger"
                        }`}
                        style={{ fontSize: "10px" }}
                      >
                        {activeQty > 0 ? `${activeQty} in stock` : "Out of stock"}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1 py-1 px-2 rounded-2"
                        style={{ fontSize: "11px" }}
                      >
                        <FaShoppingCart size={10} /> Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Bottom Actions Card */}
            <div className="admin-card shadow-sm border-0" id="product-step-save">
              <div className="admin-card-body p-3 d-flex flex-column gap-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary d-inline-flex align-items-center justify-content-center gap-2 fw-semibold py-2.5 shadow-sm rounded-3 w-100"
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span>Saving Product...</span>
                    </>
                  ) : (
                    <>
                      <FaSave size={14} /> Save Product
                    </>
                  )}
                </button>
                <Link
                  to="/admin/view-product"
                  className="btn btn-outline-secondary d-inline-flex align-items-center justify-content-center gap-2 py-2 rounded-3 w-100"
                >
                  Cancel & Return
                </Link>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
